import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { ChevronDown, ChevronUp, Target, ShieldCheck } from 'lucide-react';
import type { CandidateProfile } from '@/lib/types/candidate';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

// Utility function to get color theme based on score (0-100 scale)
const getScoreColorTheme = (score: number | null | undefined) => {
  if (score === null || score === undefined) {
    return {
      bgColor: 'bg-slate-50',
      textColor: 'text-slate-800',
      borderColor: 'border-slate-200'
    };
  }

  if (score >= 80) {
    return {
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-800',
      borderColor: 'border-emerald-200'
    };
  } else if (score >= 60) {
    return {
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-800',
      borderColor: 'border-amber-200'
    };
  } else {
    return {
      bgColor: 'bg-rose-50',
      textColor: 'text-rose-800',
      borderColor: 'border-rose-200'
    };
  }
};

// Utility function to get circle background color based on score
const getScoreCircleColor = (score: number | null | undefined | string) => {
  // Handle N/A or string values
  if (score === 'N/A' || score === null || score === undefined) {
    return 'bg-slate-400/60';
  }

  // Convert to number if it's a string
  const numScore = typeof score === 'string' ? parseFloat(score) : score;

  if (isNaN(numScore)) {
    return 'bg-slate-400/60';
  }

  if (numScore >= 70) {
    return 'bg-emerald-500/70';
  } else if (numScore >= 60) {
    return 'bg-amber-500/70';
  } else {
    return 'bg-rose-500/70';
  }
};

// Types from CandidateDetail
export type TranscriptEntry = {
  role?: string;
  type?: string;
  message?: string;
  [key: string]: any;
};

export type InterviewResult = {
  id: string;
  candidate_id: string;
  interview_id: string;
  transcript: TranscriptEntry[] | string;
  summary: string;
  status?: string;
  score?: number;
  created_at: string;
  conversation_id?: string;
  interviews?: {
    title: string;
  };
  code_review_results?: any;
  wrap_up_transcript?: TranscriptEntry[] | string;
};

export type CandidateProject = {
  name: string;
  description?: string;
  url?: string;
};

export type CandidateProfileWithProjects = CandidateProfile & {
  projects?: CandidateProject[];
};

interface ReviewTabProps {
  candidate?: CandidateProfileWithProjects;
  selectedInterview: InterviewResult | null;
  setIsCalculationsOpen?: (open: boolean) => void;
  getGradeBadgeClass?: (grade?: string) => string;
  formatChallengeTitle?: (title: string) => string;
  onAccordionChange?: (value: string | undefined) => void;
  codingAssessmentFinalScore?: number;
  codingAssessmentLoading?: boolean;
}

const slackCodeStyle = {
  backgroundColor: '#f1f2f6',
  color: '#e01e5a',
  padding: '2px 4px',
  borderRadius: '3px',
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  fontSize: '0.875em',
  border: '1px solid #e1e4e8'
};

export const ReviewTab: React.FC<ReviewTabProps> = ({
  selectedInterview,
  onAccordionChange,
  codingAssessmentFinalScore,
  codingAssessmentLoading
}) => {
  const [isCalculationsOpen, setIsCalculationsOpen] = useState(false);
  const [evaluation, setEvaluation] = useState<any>({});
  const [totalScore, setTotalScore] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedInterview) return;
    const fetchInterviewEvaluation = async () => {
      if (!selectedInterview?.id) {
        toast.error('interview not found');
        return;
      }

      const result = await fetch(
        `/api/interviews-taken/${selectedInterview.id}/evaluations`
      );
      const data = await result.json();
      if (data.error) {
        toast.error(data.error);
        return;
      }
      setEvaluation(data.data);
    };
    fetchInterviewEvaluation();
  }, [selectedInterview]);

  // Calculate total score when dependencies change
  useEffect(() => {
    if (codingAssessmentLoading) {
      setTotalScore(null);
      return;
    }

    // ! First variable is the Coderbyte Assessment Score, otherwise fallback to the older assessment type (our own hosted assessments)
    const codingAssessmentNormalizedScore = (codingAssessmentFinalScore || Math.round(
        (evaluation?.coding_quality_score +
          evaluation?.coding_style_score +
          evaluation?.coding_test_score) /
          3
    ) || 0);

    if (
      evaluation?.experience_assessment?.candidateSeniorityScore
      && evaluation?.english_proficiency?.english_proficiency_score
      && codingAssessmentNormalizedScore
    ) {
      const totalScore =
        (evaluation?.experience_assessment?.candidateSeniorityScore || 0) * EXPERIENCE_AGENT_WEIGHT +
        (evaluation?.english_proficiency?.english_proficiency_score || 50) * ENGLISH_PROFICIENCY_WEIGHT +
        (codingAssessmentNormalizedScore * CODING_ASSESSMENT_WEIGHT);

      setTotalScore(totalScore);
    }
  }, [evaluation, codingAssessmentFinalScore, codingAssessmentLoading]);

  const EXPERIENCE_AGENT_WEIGHT = 0.35;
  const ENGLISH_PROFICIENCY_WEIGHT = 0.25;
  const CODING_ASSESSMENT_WEIGHT = 0.4; 

  

  return (
    <div className="space-y-6">
      <Card className="rounded-xl border border-slate-200 bg-white gap-3">
        <CardHeader className="flex flex-row items-center gap-3">
          <CardTitle className="text-2xl">Final Score</CardTitle>
          <div
            className={`flex items-center justify-center rounded-full ${getScoreCircleColor(totalScore)} w-8 h-8`}
          >
            <p className="text-white text-sm font-semibold">
              {totalScore !== null ? Math.round(totalScore) : 'N/A'}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 leading-relaxed">
            The candidate has been reviewed on their experience in thinking,
            coding and english communication.
          </p>
          <div className="flex flex-col gap-4 py-4">
            <div className="w-full relative">
              <div
                className={`rounded-md border mt-2 transition-all duration-300 relative ${
                  isCalculationsOpen
                    ? 'h-auto opacity-100'
                    : 'h-[120px] opacity-100 overflow-hidden'
                }`}
              >
                {!isCalculationsOpen && (
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Component</TableHead>
                      <TableHead>Score (0-100)</TableHead>
                      <TableHead>Weight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>Experience Agent</TableCell>
                      <TableCell>
                        {evaluation?.experience_assessment
                          ?.candidateSeniorityScore || 'N/A'}
                      </TableCell>
                      <TableCell>{EXPERIENCE_AGENT_WEIGHT}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>English Proficiency</TableCell>
                      <TableCell>
                        {evaluation?.english_proficiency
                          ?.english_proficiency_score || `N/A`}
                      </TableCell>
                      <TableCell>{ENGLISH_PROFICIENCY_WEIGHT}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Coding Challenge</TableCell>
                      <TableCell>
                        {codingAssessmentLoading ? (
                          <span className="text-gray-400">Loading...</span>
                        ) : (
                          codingAssessmentFinalScore || '-'
                        )}
                      </TableCell>
                      <TableCell>{CODING_ASSESSMENT_WEIGHT}</TableCell>
                    </TableRow>
                    <TableRow className="font-semibold">
                      <TableCell>Final Score</TableCell>
                      <TableCell>
                        {codingAssessmentLoading ? (
                          <span className="text-gray-400">Loading...</span>
                        ) : (
                          totalScore !== null ? Math.round(totalScore) : '-'
                        )}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <div className="absolute -bottom-14 right-0 left-0 flex justify-center">
                <Button
                  onClick={() => setIsCalculationsOpen(!isCalculationsOpen)}
                  variant="outline"
                >
                  {isCalculationsOpen ? 'Less results' : 'More results'}
                  {isCalculationsOpen ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-slate-200 bg-white">
        <CardHeader>
          <CardTitle className="text-xl">Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            type="single"
            collapsible
            onValueChange={onAccordionChange}
            defaultValue="thinking"
          >
            <AccordionItem value="thinking">
              <AccordionTrigger className="flex items-center gap-2 hover:no-underline cursor-pointer">
                <div className="flex items-center w-full gap-3">
                  <div
                    className={`flex items-center justify-center rounded-full ${getScoreCircleColor(evaluation?.experience_assessment?.candidateSeniorityScore)} w-8 h-8`}
                  >
                    <p className="text-white text-sm font-semibold">
                      {evaluation?.experience_assessment
                        ?.candidateSeniorityScore || 'N/A'}
                    </p>
                  </div>
                  <h3 className="text-lg font-semibold">Thinking</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-0.5 bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold">Seniority</h4>
                  <p className="text-sm">
                    {evaluation?.experience_assessment?.candidateSeniority ||
                      '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold">Years of Experience</h4>
                  <p className="text-sm">
                    {evaluation?.experience_assessment
                      ?.candidateExperienceYears || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 col-span-2 bg-gray-100 border-2 border-gray-200 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold">Reasoning</h4>
                  <p className="text-sm">
                    {evaluation?.experience_assessment?.executiveSummary || '-'}
                  </p>
                </div>

                <div className="flex flex-col gap-0.5 col-span-2 bg-green-50 border-2 border-green-200 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold">Strengths</h4>

                  <ReactMarkdown
                    components={{
                      code: ({
                        node,
                        className,
                        children,
                        ...props
                      }) => {
                        return className ? (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        ) : (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {evaluation?.experience_assessment?.keyStrengths || '-'}
                  </ReactMarkdown>
                </div>

                <div className="flex flex-col gap-0.5 col-span-2 bg-red-50 border-2 border-red-200 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold">Weaknesses</h4>
                  <ReactMarkdown
                    components={{
                      code: ({
                        node,
                        className,
                        children,
                        ...props
                      }) => {
                        return className ? (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        ) : (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {evaluation?.experience_assessment?.developmentAreas || '-'}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="coding">
              <AccordionTrigger className="flex items-center gap-2 hover:no-underline cursor-pointer">
                <div className="flex items-center w-full gap-4">
                                      <div
                      className={`flex items-center justify-center rounded-full ${getScoreCircleColor(
                        codingAssessmentFinalScore || Math.round(
                          (evaluation?.coding_quality_score +
                            evaluation?.coding_style_score +
                            evaluation?.coding_test_score) /
                            3
                        ) || 0
                      )} w-8 h-8`}
                    >
                      <p className="text-white text-sm font-bold">
                        {codingAssessmentFinalScore || Math.round(
                          (evaluation?.coding_quality_score +
                            evaluation?.coding_style_score +
                            evaluation?.coding_test_score) /
                            3
                        ) || '-'}
                      </p>
                    </div>
                  <h3 className="text-lg font-semibold">Coding</h3>
                </div>
              </AccordionTrigger>
                             <AccordionContent className="space-y-4">
                 {codingAssessmentFinalScore ? (
                   <>
                     {/* Key Metrics Summary */}
                     <div className="grid grid-cols-3 gap-4">
                       <div className={`rounded-lg p-4 ${
                         codingAssessmentFinalScore >= 70 
                           ? 'bg-emerald-50 border border-emerald-200' 
                           : 'bg-amber-50 border border-amber-200'
                       }`}>
                         <div className="flex items-center justify-between mb-2">
                           <span className={`text-xs font-medium uppercase tracking-wider ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-700' 
                               : 'text-amber-700'
                           }`}>
                             Final Score
                           </span>
                           <Target className={`w-4 h-4 ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-600' 
                               : 'text-amber-600'
                           }`} />
                         </div>
                         <div className="flex items-baseline gap-1">
                           <span className={`text-2xl font-bold ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-700' 
                               : 'text-amber-700'
                           }`}>
                             {Math.round(codingAssessmentFinalScore)}%
                           </span>
                         </div>
                       </div>

                       <div className={`rounded-lg p-4 ${
                         codingAssessmentFinalScore >= 70 
                           ? 'bg-emerald-50 border border-emerald-200' 
                           : 'bg-amber-50 border border-amber-200'
                       }`}>
                         <div className="flex items-center justify-between mb-2">
                           <span className={`text-xs font-medium uppercase tracking-wider ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-700' 
                               : 'text-amber-700'
                           }`}>
                             Status
                           </span>
                           <ShieldCheck className={`w-4 h-4 ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-600' 
                               : 'text-amber-600'
                           }`} />
                         </div>
                         <div className="flex items-baseline gap-1">
                           <span className={`text-lg font-semibold ${
                             codingAssessmentFinalScore >= 70 
                               ? 'text-emerald-700' 
                               : 'text-amber-700'
                           }`}>
                             {codingAssessmentFinalScore >= 70 ? 'Qualified' : 'Not Qualified'}
                           </span>
                         </div>
                       </div>

                       <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                         <div className="flex items-center justify-between mb-2">
                           <span className="text-xs font-medium text-emerald-700 uppercase tracking-wider">
                             Integrity
                           </span>
                           <ShieldCheck className="w-4 h-4 text-emerald-600" />
                         </div>
                         <div className="flex items-baseline gap-1">
                           <span className="text-lg font-semibold text-emerald-700">
                             Verified
                           </span>
                         </div>
                       </div>
                     </div>

                     {/* AI Analysis */}
                     <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                       <h4 className="text-sm font-semibold text-gray-900 mb-3">AI Assessment Summary</h4>
                       <div className="space-y-2 text-sm text-gray-700">
                         <p>
                           <strong>Performance:</strong> The candidate achieved a {Math.round(codingAssessmentFinalScore)}% score on the coding assessment, 
                           {codingAssessmentFinalScore >= 80 ? ' demonstrating excellent technical proficiency' : 
                            codingAssessmentFinalScore >= 60 ? ' showing solid technical skills' : 
                            ' indicating areas for improvement in technical capabilities'}.
                         </p>
                         <p>
                           <strong>Assessment Type:</strong> Coderbyte platform evaluation with comprehensive problem-solving challenges.
                         </p>
                         <p>
                           <strong>Recommendation:</strong> {codingAssessmentFinalScore >= 70 ? 'Strong candidate for technical roles' : 'Consider additional technical training or junior-level positions'}.
                         </p>
                       </div>
                     </div>
                   </>
                 ) : (
                   <>
                     {/* Fallback to old assessment data */}
                     <div className="grid grid-cols-3 gap-4">
                {(() => {
                  const qualityTheme = getScoreColorTheme(
                    evaluation?.coding_quality_score
                  );
                  return (
                    <div
                      className={`flex flex-col gap-0.5 p-4 rounded-lg border ${qualityTheme.bgColor} ${qualityTheme.textColor} ${qualityTheme.borderColor}`}
                    >
                      <h4 className="text-sm">Code Quality</h4>
                      <p className="text-lg font-semibold">
                        {evaluation?.coding_quality_score || '-'}
                      </p>
                    </div>
                  );
                })()}
                {(() => {
                  const styleTheme = getScoreColorTheme(
                    evaluation?.coding_style_score
                  );
                  return (
                    <div
                      className={`flex flex-col gap-0.5 p-4 rounded-lg border ${styleTheme.bgColor} ${styleTheme.textColor} ${styleTheme.borderColor}`}
                    >
                      <h4 className="text-sm">Code Style</h4>
                      <p className="text-lg font-semibold">
                        {evaluation?.coding_style_score || '-'}
                      </p>
                    </div>
                  );
                })()}
                {(() => {
                  const completionTheme = getScoreColorTheme(
                    evaluation?.coding_test_score
                  );
                  return (
                    <div
                      className={`flex flex-col gap-0.5 p-4 rounded-lg border ${completionTheme.bgColor} ${completionTheme.textColor} ${completionTheme.borderColor}`}
                    >
                      <h4 className="text-sm">Test passes rate</h4>
                      <p className="text-lg font-semibold">
                        {evaluation?.coding_test_score || '-'}%
                      </p>
                    </div>
                  );
                })()}
                     </div>

                     {/* Legacy evaluation reasoning */}
                     <div className="w-full bg-gray-50 border-2 border-gray-200 p-4 rounded-lg">
                  <ReactMarkdown
                    components={{
                      code: ({
                        node,
                        className,
                        children,
                        ...props
                      }) => {
                        return className ? (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        ) : (
                          <code style={slackCodeStyle} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {evaluation?.coding_evaluation_reasoning || '-'}
                  </ReactMarkdown>
                </div>
                   </>
                 )}
              </AccordionContent>
            </AccordionItem>

            {/* Coderbyte Assessment Results */}
            {evaluation?.coding_assessments?.[0] && (
              <AccordionItem value="coderbyte-assessment">
                <AccordionTrigger className="flex items-center gap-2 hover:no-underline cursor-pointer">
                  <div className="flex items-center w-full gap-4">
                    <div
                      className={`flex items-center justify-center rounded-full w-8 h-8 ${
                        evaluation.coding_assessments[0]
                          .coding_assessment_results?.[0]?.final_score >= 70
                          ? 'bg-emerald-500/70'
                          : 'bg-rose-500/70'
                      }`}
                    >
                      <p className="text-white text-sm font-bold">
                        {evaluation.coding_assessments[0]
                          .coding_assessment_results?.[0]?.final_score || '-'}
                      </p>
                    </div>
                    <h3 className="text-lg font-semibold">
                      Coding Assessment (
                      {evaluation.coding_assessments[0].status})
                    </h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {evaluation.coding_assessments[0].status === 'completed' &&
                  evaluation.coding_assessments[0]
                    .coding_assessment_results?.[0] ? (
                    <div className="space-y-4">
                      {/* Overall Scores */}
                      <div className="grid grid-cols-3 gap-4">
                        {(() => {
                          const finalScore =
                            evaluation.coding_assessments[0]
                              .coding_assessment_results[0].final_score;
                          const finalTheme = getScoreColorTheme(finalScore);
                          return (
                            <div
                              className={`flex flex-col gap-0.5 p-4 rounded-lg border ${finalTheme.bgColor} ${finalTheme.textColor} ${finalTheme.borderColor}`}
                            >
                              <h4 className="text-sm font-semibold">
                                Final Score
                              </h4>
                              <p className="text-2xl font-bold">
                                {finalScore}%
                              </p>
                              <p className="text-xs opacity-75">
                                {evaluation.coding_assessments[0]
                                  .coding_assessment_results[0].qualified
                                  ? 'Qualified'
                                  : 'Not Qualified'}
                              </p>
                            </div>
                          );
                        })()}

                        {(() => {
                          const codeScore =
                            evaluation.coding_assessments[0]
                              .coding_assessment_results[0].code_score;
                          const codeTheme = getScoreColorTheme(codeScore);
                          return (
                            <div
                              className={`flex flex-col gap-0.5 p-4 rounded-lg border ${codeTheme.bgColor} ${codeTheme.textColor} ${codeTheme.borderColor}`}
                            >
                              <h4 className="text-sm font-semibold">
                                Coding Score
                              </h4>
                              <p className="text-2xl font-bold">{codeScore}%</p>
                            </div>
                          );
                        })()}

                        {(() => {
                          const mcScore =
                            evaluation.coding_assessments[0]
                              .coding_assessment_results[0].mc_score;
                          const mcTheme = getScoreColorTheme(mcScore);
                          return (
                            <div
                              className={`flex flex-col gap-0.5 p-4 rounded-lg border ${mcTheme.bgColor} ${mcTheme.textColor} ${mcTheme.borderColor}`}
                            >
                              <h4 className="text-sm font-semibold">
                                Multiple Choice
                              </h4>
                              <p className="text-2xl font-bold">{mcScore}%</p>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Challenge Details */}
                      {evaluation.coding_assessments[0]
                        .coding_assessment_results[0].challenge_details && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="text-sm font-semibold mb-2">
                            Challenges Completed
                          </h4>
                          <p className="text-sm text-gray-600">
                            {
                              evaluation.coding_assessments[0]
                                .coding_assessment_results[0].total_challenges
                            }{' '}
                            challenges
                          </p>
                        </div>
                      )}

                      {/* Cheating Detection */}
                      {evaluation.coding_assessments[0]
                        .coding_assessment_results[0].cheating_flag && (
                        <div className="bg-rose-50 border border-rose-200 p-4 rounded-lg">
                          <h4 className="text-sm font-semibold text-rose-800 mb-1">
                            ⚠️ Integrity Flag
                          </h4>
                          <p className="text-sm text-rose-700">
                            {
                              evaluation.coding_assessments[0]
                                .coding_assessment_results[0].cheating_flag
                            }
                          </p>
                        </div>
                      )}

                      {/* Scorecard */}
                      {evaluation.coding_assessments[0]
                        .coding_assessment_results[0].scorecard && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="text-sm font-semibold mb-2">
                            Skills Breakdown
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(
                              evaluation.coding_assessments[0]
                                .coding_assessment_results[0].scorecard
                            ).map(([skill, score]) => (
                              <div
                                key={skill}
                                className="flex justify-between text-sm"
                              >
                                <span className="text-gray-600">{skill}:</span>
                                <span className="font-medium">
                                  {score as number}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        Coding assessment{' '}
                        {evaluation.coding_assessments[0].status}
                      </p>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};
